import React, { useMemo } from 'react'
import { MediaCategory, StoredMediaCategory } from '../../../types/Media'
import { useTranslation } from 'react-i18next'
import * as yup from 'yup'
import { useStoreData } from '../../../hooks/useStoreData'
import Input from '../../../components/Input'
import Select from '../../../components/Select'
import Button from '../../../components/Button'
import { treeOptions } from '../../../helpers/treeOptions'
import { ApiRoutes } from '../../../services/ApiService'
import { showToast } from '../../../services/ToastService'

type CreateCategoryModalProps = {
    categories: StoredMediaCategory[]
    onNewCategory?: (newCategoryId: number) => void
}

const CreateCategoryModal: React.FC<CreateCategoryModalProps> = ({
    categories,
    onNewCategory,
}) => {
    const { t } = useTranslation(['settings'])

    const storeCategorySchema = useMemo(() => {
        return {
            name: yup.string().required().min(3),
            parent_id: yup.string().oneOf(categories.map((c) => c.id + '')),
        }
    }, [categories])

    const { element, isValid, changeProp, submit } =
        useStoreData<StoredMediaCategory>(
            ApiRoutes.MEDIA_CATEGORIES,
            storeCategorySchema
        )

    const submitCategory = () => {
        submit()
            .then((res) => {
                if (res.status === 201) {
                    const id = res.data?.data?.id
                    if (onNewCategory && id) onNewCategory(Number(id))
                    changeProp({ name: '', parent_id: null })
                    showToast(t('categories.create_modal.success'))
                } else {
                    showToast(t('categories.create_modal.error'), 'error')
                }
            })
            .catch(() => {
                showToast(t('categories.create_modal.error'), 'error')
            })
    }

    const selectOptions = useMemo(() => {
        return treeOptions<MediaCategory>(
            categories,
            null,
            'name',
            'id',
            'parent_id',
            'id',
            0
        )
    }, [categories])

    return (
        <div>
            <Input
                type={'text'}
                placeholder={t('categories.labels.name')}
                property={'name'}
                value={element.name ?? ''}
                onChange={(_, value) => changeProp({ name: value })}
                classes={'zr-my-3 zr-mr-4'}
            />
            <Select
                options={selectOptions}
                onSelect={(_, value) =>
                    changeProp({ parent_id: parseInt(value) })
                }
                property={'parent_id'}
                value={element.parent_id?.toString() ?? ''}
                placeholder={t('categories.labels.parent')}
                classes={'zr-my-3 zr-mr-4'}
            />
            <Button
                hierarchy={'primary'}
                text={t('categories.create_modal.action')}
                classes={'zr-w-100 zr-mr-4'}
                fn={submitCategory}
                disabled={!isValid}
            />
        </div>
    )
}

export default CreateCategoryModal
